import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type BearriMotionName } from './motionAssets';
import CardboardCutout, { PIN_GEOMETRY } from './CardboardCutout';
import useCardboardTilt from './useCardboardTilt';
import './chapterHeading.css';

export type BearriBadgeName = 'channels' | 'research' | 'writing' | 'mail' | 'sponsors' | 'rewards' | 'reading';

/** One art family for workspace headings. Marketing clips keep their own renderer. */
export const BADGE_FOR_MOTION: Record<BearriMotionName, BearriBadgeName> = {
  search: 'research', scout: 'research', thinking: 'research',
  writing: 'writing', reading: 'reading', mail: 'mail',
  contract: 'sponsors', celebrate: 'rewards', loving: 'rewards', berry: 'rewards',
  wave: 'channels', idle: 'channels', sleeping: 'channels',
};

/** An earned moment (a won deal): the cut-out spins once; `key` changes fire it again. */
export type BearriCue = { name: BearriMotionName; key: number };

type Phase = 'rest' | 'working' | 'settling';

/** Motion never leaves rest where the person asked for quiet, in a quiet preview, or in a hidden tab. */
const quietHere = (el: HTMLElement | null) =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
  || Boolean(el?.closest('[data-quiet=true]')) || document.hidden;

/**
 * The pin is the page's one bear, and it is always the NEW drawing.
 *
 * While the page does page-level work (`busy` with a `motion`) the cut-out itself works: it
 * rocks gently on its foot, and when the work ends it lands again. A `cue` (an earned moment:
 * a won deal) spins the cardboard once. It used to fade to a video clip instead — but every
 * clip is an older drawing (the Search_Bear bust, the pink standing bears), so each page
 * flashed a different, older bear while it loaded. The motion name is kept for the callers;
 * it only says the page is working. Card-level work animates in the card's own scene.
 *
 * The print composites normally: its white corners are cut away by the outline's clip-path,
 * so it needs no multiply — which had dimmed every bear to the desk's value and let the desk's
 * grain show through its body.
 */
export default function BearriBadge({ name, busy = false, motion, cue, className = '', label = 'Spin Bearri cut-out' }: {
  name: BearriBadgeName; busy?: boolean; motion?: BearriMotionName; cue?: BearriCue; className?: string; label?:string;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const artRef = useRef<HTMLImageElement>(null);
  const backRef = useRef<HTMLImageElement>(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [backLoaded, setBackLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [phase, setPhase] = useState<Phase>('rest');
  const [landings, setLandings] = useState(0);
  const tilt = useCardboardTilt(ref, loaded && !failed && visible && phase === 'rest');
  const spinLock = useRef(false);
  const lastCue = useRef<number>();
  const endSpin = useCallback(() => {spinLock.current=false;setSpinning(false);}, []);
  useEffect(() => {
    const target = ref.current;
    if (!target) return;
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return; }
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.15 });
    observer.observe(target);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    // Cached images may already be ready when effects run (including hot reload).
    setLoaded(Boolean(artRef.current?.complete && artRef.current.naturalWidth));
    setBackLoaded(Boolean(backRef.current?.complete && backRef.current.naturalWidth));
    setFailed(false); spinLock.current=false; setSpinning(false);
  }, [name]);
  useEffect(() => {
    if (!spinning) return;
    // Safety cleanup if motion preferences change or animationend is lost.
    const timer=window.setTimeout(endSpin,2200);
    return () => window.clearTimeout(timer);
  }, [spinning,endSpin]);

  // ── the living pin ──────────────────────────────────────────────────────
  const wantsWork = busy && Boolean(motion) && visible && loaded && !failed;
  useEffect(() => {
    if (wantsWork && phase === 'rest' && !quietHere(ref.current)) setPhase('working');
    else if (!wantsWork && phase === 'working') setPhase('settling');
  }, [wantsWork, motion, phase]);
  useEffect(() => {
    if (!cue || cue.key === lastCue.current) return;
    lastCue.current = cue.key;
    // the celebration is the cardboard itself spinning once — never a clip of another drawing
    if (!visible || !loaded || !backLoaded || failed || spinLock.current || quietHere(ref.current)) return;
    spinLock.current = true; setSpinning(true);
  }, [cue, visible, loaded, backLoaded, failed]);
  useEffect(() => {
    if (phase !== 'settling') return;
    // the rocking stops, then the cardboard lands again: the re-landing is the completion beat
    const timer = window.setTimeout(() => { setPhase('rest'); setLandings(n => n + 1); }, 180);
    return () => window.clearTimeout(timer);
  }, [phase]);

  const spin = (event:React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    // Tilt can always use the loaded front. Never spin onto an unloaded rear, and never while the pin works.
    if (!loaded || !backLoaded || failed || spinLock.current || phase !== 'rest' || quietHere(ref.current)) return;
    spinLock.current=true; setSpinning(true);
  };
  const hasDepth = spinning || tilt.active;
  return <button ref={ref} type="button" aria-label={label} title="Give Bearri a spin" data-bearri-spin
    disabled={!loaded || failed} onClick={spin} {...tilt.handlers}
    className={`br-bearri-badge ${className}`} data-badge={name}
    data-visible={visible} data-ready={loaded} data-working={busy} data-phase={phase} data-spinning={spinning} data-depth={hasDepth}>
    {/* each landing alternates the keyframe name so the settle plays again without remounting the prints */}
    <span className="br-bearri-badge__pose" aria-hidden="true" style={landings && phase !== 'working' ? { animationName: landings % 2 ? 'br-pin-land' : 'br-pin-settle' } : undefined}>
    {hasDepth && <span className="br-bearri-badge__shadow"><span style={{clipPath:PIN_GEOMETRY[name].clip}} /></span>}
    <span ref={tilt.surfaceRef} className="br-bearri-badge__tilt">
    {!failed && <CardboardCutout key={name} name={name} withEdges={hasDepth}
      front={<img ref={artRef} className="br-bearri-badge__print" src={`/brand/pins/${name}.webp`} alt="" width={512} height={512}
        loading="lazy" decoding="async" draggable={false}
        onLoad={() => setLoaded(true)} onError={() => {setFailed(true);endSpin();}} />}
      backRef={backRef} onBackLoad={() => setBackLoaded(true)} onBackError={() => {setBackLoaded(false);endSpin();}}
      className={spinning ? 'br-cardboard-cutout--spin' : ''}
      onAnimationEnd={event => {if(event.target===event.currentTarget) endSpin();}} />}
    </span>
    </span>
  </button>;
}
